import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import PromoCodeContext from '../../../contexts/PromoCodeContext';
import useGetPlansInfo from '../../../hooks/useGetPlansInfo';
import GlobalTheme from '../../../styled/GlobalTheme';
import roles from '../../../utils/constants/roles';
import sanitizePlans from '../sanitizePlans';
import PlanDetails from './PlanDetails';
import PlanOption from './PlanOption';
import { ModalTableIOS } from '../../../components/CompareTableIOS';
import { WebViewContext } from '../../../contexts/WebViewContext';
import PromoCodeProvider from '../../../services/entities/PromoCodeProvider';

const Container = styled.div`
  position: relative;
`;

const PlansContainer = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 32px;
  width: 100%;
  opacity: ${({ showDetails }) => (showDetails ? '0.25' : '1')};
  ${GlobalTheme.breakpoints.down('sm')} {
    flex-direction: column;
  }
`;

const PlansSelector = ({
  planSelected, setPlanSelected, isMonthly, hasSelector, isModal,
}) => {
  const intl = useIntl();
  const { PromoCode } = useContext(PromoCodeContext);
  const plansInfo = useGetPlansInfo();
  const [priceY, setPriceY] = useState(0);
  const [priceM, setPriceM] = useState(0);
  const [currencyCode, setCurrencyCode] = useState('');
  const [showDetails, setShowDetails] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const { isAppIOS } = useContext(WebViewContext);
  const [existPriceIOS, setExistPriceIOS] = useState(false);
  const [trialDaysM, setTrialDaysM] = useState(0);
  const [trialDaysY, setTrialDaysY] = useState(0);

  const proPrice = () => (
    isMonthly ? intl.formatMessage({
      id: 'planOption.price.monthly',
      defaultMessage: '{cur} {price} month',
    }, {
      price: priceM,
      cur: currencyCode,
    }) : intl.formatMessage({
      id: 'planOption.price.yearly',
      defaultMessage: '{cur} {price} year',
    }, {
      price: priceY,
      cur: currencyCode,
    })
  );

  const setPriceDefault = () => {
    const [monthlyPlan, yearlyPlan] = sanitizePlans(PromoCode);
    setPriceM(monthlyPlan.realPrice);
    setPriceY(yearlyPlan.realPrice);
    setCurrencyCode(monthlyPlan.currencyCode);
    setTrialDaysM(monthlyPlan.trialDuration);
    setTrialDaysY(yearlyPlan.trialDuration);
  };

  const setPricePlansIOSDefault = async () => {
    const currencyCodeStorage = window.localStorage.getItem('currencyCode');
    const countryCodeStorage = window.localStorage.getItem('countryCode');
    const response = await PromoCodeProvider.getPlansIOS(currencyCodeStorage, countryCodeStorage);
    if (response.success && !existPriceIOS) {
      const planMonthly = response.data.plans.filter((plan) => (plan.isMonthly));
      const planYearly = response.data.plans.filter((plan) => (!plan.isMonthly));

      setPriceM(planMonthly[0].realPrice);
      setPriceY(planYearly[0].realPrice);
      setCurrencyCode(currencyCodeStorage);
    }
  };

  useEffect(() => {
    if (!isAppIOS) {
      setPriceDefault();
    }
    if (PromoCode.plans) {
      // Temporarily for quick fix
      setTrialDaysM(14);
      setTrialDaysY(14);
    }
  }, [PromoCode, trialDaysM, trialDaysY]);

  useEffect(() => {
    window.setPricesIOS = (priceMonthly, priceYearly, currencyCodeIOS) => {
      if (priceMonthly) {
        setExistPriceIOS(true);
        setPriceM(parseFloat(priceMonthly));
        setPriceY(parseFloat(priceYearly));
        setCurrencyCode(currencyCodeIOS);
        return;
      }
      setPricePlansIOSDefault();
    };
  }, []);

  return (
    <Container>
      {showDetails && (
        <PlanDetails
          title={plansInfo[showDetails].title}
          features={plansInfo[showDetails].features}
          price={showDetails === 'pro' ? proPrice() : intl.formatMessage({
            id: 'k.free',
            defaultMessage: 'Free',
          })}
          details={plansInfo[showDetails].details}
          comingSoon={plansInfo[showDetails].comingSoon}
          recommended={plansInfo[showDetails].recommended}
          color={plansInfo[showDetails].color}
          setShowDetails={setShowDetails}
          detailsDescription={plansInfo[showDetails].detailsDescription}
        />
      )}
      <PlansContainer
        showDetails={showDetails}
      >
        <PlanOption
          title={plansInfo.pro.title}
          description={plansInfo.pro.description}
          features={plansInfo.pro.features}
          price={isMonthly ? priceM : priceY}
          isMonthly={isMonthly}
          planId="pro"
          onClick={() => setPlanSelected(roles.PRO)}
          selected={planSelected === roles.PRO}
          recommended
          color={plansInfo.pro.color}
          details={plansInfo.pro.details}
          detailsDescription={plansInfo.pro.detailsDescription}
          setShowDetails={setShowDetailsModal}
          hasSelector={hasSelector}
          currencyCode={currencyCode}
          trialDays={isMonthly ? trialDaysM : trialDaysY}
        />
      </PlansContainer>
      <ModalTableIOS
        open={showDetailsModal}
        setOpen={setShowDetailsModal}
      />
    </Container>

  );
};

PlansSelector.propTypes = {
  planSelected: PropTypes.string,
  setPlanSelected: PropTypes.func,
  isMonthly: PropTypes.bool.isRequired,
  hasSelector: PropTypes.bool,
  isModal: PropTypes.bool,
};

PlansSelector.defaultProps = {
  planSelected: roles.PRO,
  setPlanSelected: () => { },
  hasSelector: false,
};

export default PlansSelector;
